import React from 'react'
import { DataTable, DataTableColumn } from './DataTable'
import { TableCustomizationMenu } from './TableCustomizationMenu'
import { useTableCustomization, TableCustomizationController } from '@/shared/hooks/useTableCustomization'

interface DataTableWithCustomizationProps<T> {
  // Props básicas do DataTable
  columns: DataTableColumn<T>[]
  data: T[]
  keyExtractor: (item: T) => string
  onRowClick?: (item: T) => void
  
  // Bulk selection props (optional)
  selectable?: boolean
  selectedIds?: Set<string>
  onToggleSelection?: (id: string) => void
  onToggleAll?: () => void
  isAllSelected?: boolean
  isSomeSelected?: boolean
  // Optional predicate to determine whether a given row is selectable
  isItemSelectable?: (item: T) => boolean
  
  // Customization props
  storageKey: string
  showCustomizationMenu?: boolean
  controller?: TableCustomizationController
  // Optional per-row stamp watermark
  getRowStamp?: (item: T) => string | null | undefined
}

/**
 * DataTable com funcionalidades de customização integradas:
 * - Mostrar/ocultar colunas
 * - Reordenar colunas (drag & drop)
 * - Redimensionar colunas
 * - Densidade da tabela (compacta, normal, confortável)
 * - Persistência automática das preferências
 */
export function DataTableWithCustomization<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  selectable,
  selectedIds,
  onToggleSelection,
  onToggleAll,
  isAllSelected,
  isSomeSelected,
  isItemSelectable,
  storageKey,
  showCustomizationMenu = true,
  controller,
  getRowStamp,
}: DataTableWithCustomizationProps<T>) {
  // Use external controller when provided, otherwise create internal controller
  const internalController = useTableCustomization({
    storageKey,
    defaultColumns: columns.map(c => c.key)
  })

  const {
    settings,
    visibleColumns,
    toggleColumn,
    setColumnWidth,
    reorderColumns,
    setDensity,
    resetToDefaults,
    getDensityClasses
  } = controller ?? internalController

  // Preparar dados para o menu de customização, respeitando a ordem salva
  const menuColumns = columns
    .map(col => ({
      key: col.key,
      label: col.label,
      visible: settings.columns[col.key]?.visible !== false,
      order: settings.columns[col.key]?.order ?? 9999
    }))
    .sort((a, b) => a.order - b.order)
    .map(({ order: _order, ...rest }) => rest)

  return (
    <div className="space-y-3">
      {/* Menu de customização */}
      {showCustomizationMenu && (
        <div className="flex justify-end">
          <TableCustomizationMenu
            columns={menuColumns}
            density={settings.density}
            onToggleColumn={toggleColumn}
            onReorderColumns={reorderColumns}
            onChangeDensity={setDensity}
            onReset={resetToDefaults}
          />
        </div>
      )}

      {/* Tabela com customização aplicada */}
      <DataTable
        columns={columns}
        data={data}
        keyExtractor={keyExtractor}
        onRowClick={onRowClick}
        selectable={selectable}
        selectedIds={selectedIds}
        onToggleSelection={onToggleSelection}
        onToggleAll={onToggleAll}
        isAllSelected={isAllSelected}
        isSomeSelected={isSomeSelected}
        isItemSelectable={isItemSelectable}
        getRowStamp={getRowStamp}
        customization={{
          columnSettings: settings.columns,
          densityClasses: getDensityClasses(),
          onColumnResize: setColumnWidth
        }}
      />
    </div>
  )
}
